import { ArrowRight, CheckCircle2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { ApplicationDetail, UserProfile } from 'types/api'

import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import {
  calculateApplicationCompletion,
  calculateOverallCompletion,
  calculateProfileCompletion
} from '@features/welcome/utils/completionCalculator'

interface Props {
  profile?: UserProfile
  application?: ApplicationDetail
  className?: string
}

export function SetupProgressCard({ profile, application, className }: Props) {
  const { t } = useTranslation('userDashboard')

  const overall = calculateOverallCompletion(profile, application)
  const steps = [
    { key: 'profile', pct: calculateProfileCompletion(profile) },
    { key: 'application', pct: calculateApplicationCompletion(application) }
  ]
  const done = overall === 100

  return (
    <Card className={cn('h-full', className)}>
      <CardContent className="p-6">
        <div className="flex items-baseline justify-between">
          <h3 className="font-display text-lg font-bold">{t('setup.title')}</h3>
          <span className="text-2xl font-bold text-primary">{overall}%</span>
        </div>

        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-ink/[0.06]">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${overall}%` }}
          />
        </div>

        <ul className="mt-5 space-y-3">
          {steps.map((step) => (
            <li key={step.key} className="flex items-center gap-3">
              <span
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
                  step.pct === 100
                    ? 'bg-primary text-white'
                    : 'bg-ink/10 text-ink/50'
                )}
              >
                {step.pct === 100 ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  `${step.pct}%`
                )}
              </span>
              <span className="text-sm font-medium text-ink/80">
                {t(`setup.${step.key}`)}
              </span>
            </li>
          ))}
        </ul>

        {done ? (
          <p className="mt-5 text-sm font-medium text-primary">
            {t('setup.complete')}
          </p>
        ) : (
          <Link
            to="/user/welcome"
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
          >
            {t('setup.continue')} <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </CardContent>
    </Card>
  )
}
